// src/components/ReviewForm.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Star,
  Send,
  AlertCircle,
  CheckCircle,
  X,
  User,
  MessageSquare,
  Award,
  Clock,
  EyeOff
} from 'lucide-react';
import LogamiLogoSpinner from './ui/LogamiLogoSpinner';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import reviewService from '../services/reviewService';

const ReviewForm = ({ onClose, onReviewSubmitted }) => {
    const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    rating: 0,
    title: '',
    comment: '',
    plan: ''
  });
  const [hoveredRating, setHoveredRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [existingReview, setExistingReview] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
      if (currentUser) {
        checkExistingReview();
      }
    });

    return () => unsubscribe();
  }, []);

  const checkExistingReview = async () => {
    try {
      const review = await reviewService.getUserReview();
      setExistingReview(review);
    } catch (error) {
      console.error('Error checking existing review:', error);
    }
  };

 const validateForm = () => {
  const newErrors = {};

  if (formData.rating === 0) {
    newErrors.rating = t('reviewForm.error.rating');
  }

  if (formData.comment.trim().length < 10) {
    newErrors.comment = t('reviewForm.error.commentMin');
  }

  if (formData.comment.trim().length > 500) {
    newErrors.comment = t('reviewForm.error.commentMax');
  }

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await reviewService.submitReview(formData);
      setSubmitStatus('success');

      // Reset form
      setFormData({
        rating: 0,
        title: '',
        comment: '',
        plan: ''
      });

      // Notify parent component
      if (onReviewSubmitted) {
        onReviewSubmitted();
      }

      // Auto-close after success
      setTimeout(() => {
        if (onClose) onClose();
      }, 2000);

    } catch (error) {
      console.error('Error submitting review:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRatingClick = (rating) => {
    setFormData(prev => ({ ...prev, rating }));
    setErrors(prev => ({ ...prev, rating: null }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: null }));
  };

  const renderStars = () => {
    return Array.from({ length: 5 }, (_, i) => {
      const starNumber = i + 1;
      const isFilled = starNumber <= (hoveredRating || formData.rating);
      const isHovered = starNumber <= hoveredRating;

      return (
        <button
          key={i}
          type="button"
          onClick={() => handleRatingClick(starNumber)}
          onMouseEnter={() => setHoveredRating(starNumber)}
          onMouseLeave={() => setHoveredRating(0)}
          className="focus:outline-none transition-all p-1"
        >
          <Star
            className={`w-8 h-8 transition-all ${
              isFilled 
                ? 'text-black fill-current' 
                : isHovered
                  ? 'text-gray-500'
                  : 'text-gray-300'
            }`}
            style={{
              filter: isHovered && !isFilled ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' : 'none'
            }}
          />
        </button>
      );
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
          <div className="animate-pulse text-center">
            <div className="w-16 h-16 bg-gray-200 rounded-full mx-auto mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  // Not authenticated
 if (!user) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
        <div className="text-center">
          <User className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            {t('reviewForm.loginRequired')}
          </h3>
          <p className="text-gray-600 mb-6">
            {t('reviewForm.loginMessage')}
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
            >
              {t('reviewForm.close')}
            </button>
            <button
              onClick={() => {
                window.location.href = '/auth?mode=signup';
              }}
              className="flex-1 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-900 transition-colors"
            >
              {t('reviewForm.login')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

  // Already reviewed
 if (existingReview) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
        <div className="text-center">
          <CheckCircle className="w-16 h-16 text-black mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            {t('reviewForm.alreadyReviewed')}
          </h3>
          <p className="text-gray-600 mb-4">
            {t('reviewForm.alreadyReviewedMessage')}
          </p>

          {/* Show existing review */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
            <div className="flex items-center gap-2 mb-2">
              {Array.from({ length: 5 }, (_, i) => (
                <Star
                  key={i}
                  className={`w-4 h-4 ${
                    i < existingReview.rating 
                      ? 'text-black fill-current' 
                      : 'text-gray-300'
                  }`}
                />
              ))}
              <span className="text-sm text-gray-500">
                {reviewService.formatRelativeTime(existingReview.createdAt)}
              </span>
            </div>
            {existingReview.title && (
              <h4 className="font-semibold text-gray-900 mb-1">
                {existingReview.title}
              </h4>
            )}
            <p className="text-gray-700 text-sm">
              "{existingReview.comment}"
            </p>
          </div>

          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-900 transition-colors"
          >
            {t('reviewForm.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-8 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto overflow-x-hidden">

       {/* Header */}
<div className="flex items-center justify-between mb-6">
  <div className="flex items-center gap-3">
    <Award className="w-8 h-8 text-black" />
    <h3 className="text-2xl font-bold text-gray-900">
      {t('reviewForm.title')}
    </h3>
  </div>
  <button
    onClick={onClose}
    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
  >
    <X className="w-5 h-5 text-gray-500" />
  </button>
</div>

        {/* Success/Error Messages */}
{submitStatus === 'success' && (
  <div className="bg-gray-100 border border-gray-300 rounded-lg p-4 mb-6">
    <div className="flex items-center gap-3">
      <CheckCircle className="w-5 h-5 text-black" />
      <p className="text-gray-900">
        {t('reviewForm.successMessage')}
      </p>
    </div>
  </div>
)}

{submitStatus === 'error' && (
  <div className="bg-gray-100 border border-gray-300 rounded-lg p-4 mb-6">
    <div className="flex items-center gap-3">
      <AlertCircle className="w-5 h-5 text-black" />
      <p className="text-gray-900">
        {t('reviewForm.errorMessage')}
      </p>
    </div>
  </div>
)}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">

  {/* Rating */}
  <div>
    <label className="block text-sm font-medium text-gray-900 mb-3">
      {t('reviewForm.ratingLabel')}
    </label>
    <div className="flex items-center gap-2 mb-2">
      {renderStars()}
    </div>
    {hoveredRating > 0 && (
      <p className="text-sm text-gray-600">
        {hoveredRating === 1 && t('reviewForm.ratingVeryBad')}
        {hoveredRating === 2 && t('reviewForm.ratingBad')}
        {hoveredRating === 3 && t('reviewForm.ratingAverage')}
        {hoveredRating === 4 && t('reviewForm.ratingGood')}
        {hoveredRating === 5 && t('reviewForm.ratingExcellent')}
      </p>
    )}
    {errors.rating && (
      <p className="text-gray-700 text-sm mt-1">{errors.rating}</p>
    )}
  </div>

  {/* Title (Optional) */}
  <div>
    <label htmlFor="title" className="block text-sm font-medium text-gray-900 mb-2">
      {t('reviewForm.titleLabel')}
    </label>
    <input
      type="text"
      id="title"
      value={formData.title}
      onChange={(e) => handleInputChange('title', e.target.value)}
      placeholder={t('reviewForm.titlePlaceholder')}
      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      maxLength={100}
    />
    <p className="text-xs text-gray-500 mt-1">
      {t('reviewForm.titleCounter', { count: formData.title.length })}
    </p>
  </div>

  {/* Comment */}
  <div>
    <label htmlFor="comment" className="block text-sm font-medium text-gray-900 mb-2">
      {t('reviewForm.commentLabel')}
    </label>
    <textarea
      id="comment"
      value={formData.comment}
      onChange={(e) => handleInputChange('comment', e.target.value)}
      placeholder={t('reviewForm.commentPlaceholder')}
      rows={4}
      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black resize-none"
      maxLength={500}
    />
    <div className="flex justify-between items-center mt-1">
      <p className="text-xs text-gray-500">
        {t('reviewForm.commentMinLength')}
      </p>
      <p className="text-xs text-gray-500">
        {t('reviewForm.commentCounter', { count: formData.comment.length })}
      </p>
    </div>
    {errors.comment && (
      <p className="text-red-600 text-sm mt-1">{errors.comment}</p>
    )}
  </div>

  {/* Plan (Optional) */}
  <div>
    <label htmlFor="plan" className="block text-sm font-medium text-gray-900 mb-2">
      {t('reviewForm.planLabel')}
    </label>
    <select
      id="plan"
      value={formData.plan}
      onChange={(e) => handleInputChange('plan', e.target.value)}
      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
    >
      <option value="">{t('reviewForm.planSelect')}</option>
      <option value="Monatlich">{t('reviewForm.planMonthly')}</option>
      <option value="3 Monate">{t('reviewForm.plan3Months')}</option>
    </select>
  </div>

  {/* Submit Button */}
  <div className="flex gap-3 pt-4">
    <button
      type="button"
      onClick={onClose}
      className="flex-1 px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-medium"
    >
      {t('reviewForm.cancel')}
    </button>
    <button
      type="submit"
      disabled={isSubmitting || submitStatus === 'success'}
      className="flex-1 px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
    >
      {isSubmitting ? (
        <>
          <LogamiLogoSpinner size="sm" decorative={false} alt="Logami loading" />
          {t('reviewForm.submitting')}
        </>
      ) : (
        <>
          <Send className="w-4 h-4" />
          {t('reviewForm.submit')}
        </>
      )}
    </button>
  </div>
</form>

       {/* Privacy Note */}
<div className="mt-6 p-4 bg-gray-100 border border-gray-300 rounded-lg">
  <p className="text-xs text-gray-700" dangerouslySetInnerHTML={{ __html: t('reviewForm.privacyNote') }} />
</div>
      </div>
    </div>
  );
};

export default ReviewForm;